import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const RoiCircle = ({ targetPercent, text }) => {
  const [percent, setPercent] = useState(0);
  const circleRef = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          let current = 0;
          const step = () => {
            if (current < targetPercent) {
              current += 1;
              setPercent(current);
              requestAnimationFrame(step);
            } else {
              setPercent(targetPercent);
            }
          };
          step();
        }
      },
      { threshold: 0.6 }
    );

    if (circleRef.current) {
      observer.observe(circleRef.current);
    }

    return () => observer.disconnect();
  }, [targetPercent]);

  const radius = 100;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div
      ref={circleRef}
      style={{
        width: '260px',
        height: '260px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        boxShadow: '0 12px 35px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        padding: '20px',
      }}
    >
      <svg width="100%" height="100%" viewBox="0 0 240 240" style={{ position: 'absolute', top: 0, left: 0 }}>
        <circle
          cx="120"
          cy="120"
          r={radius}
          fill="none"
          stroke="#eee"
          strokeWidth="12"
        />
        <circle
          cx="120"
          cy="120"
          r={radius}
          fill="none"
          stroke="#0077b6"
          strokeWidth="12"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 120 120)"
        />
      </svg>
      <div style={{ zIndex: 1 }}>
        <h3 style={{ fontSize: '2.5rem', margin: 0, color: '#0077b6', fontWeight: '700' }}>
          {percent}%
        </h3>
      </div>
      <p style={{ 
        fontSize: '1rem', 
        width: '80%', 
        textAlign: 'center', 
        marginTop: '15px', 
        zIndex: 1,
        color: '#333',
        lineHeight: '1.5',
        fontWeight: '500'
      }}>
        {text}
      </p>
    </div>
  );
};

const RoiImpactSection = () => {
  return (
    <section
      style={{
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
        padding: '100px 5%',
        background: 'linear-gradient(120deg, #f4f7f9, #ffffff)',
        fontFamily: 'Poppins, sans-serif',
        textAlign: 'center',
        boxShadow: 'inset 0 0 50px rgba(0, 100, 150, 0.05)',
        boxSizing: 'border-box',
      }}
    >
      {/* SEO-OPTIMIZED H2 with NEW KEYWORDS */}
      <h2 style={{ 
        fontSize: 'clamp(1.9rem, 4vw, 2.8rem)', 
        fontWeight: '700', 
        marginBottom: '25px', 
        color: '#1A1A1A',
        lineHeight: '1.3'
      }}>
        Benefits of Medical Billing Outsourcing for Clinics: ROI & Cost Reduction
      </h2>

      {/* Supporting paragraph with keywords */}
      <p style={{
        fontSize: '1.1rem',
        maxWidth: '900px',
        margin: '0 auto 50px',
        lineHeight: '1.7',
        color: '#444',
      }}>
        Discover why small practices and independent physicians choose{' '}
        <Link 
          to="/medical-billing-outsourcing-small-practices"
          style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
        >
          medical billing outsourcing
        </Link>{' '}
        to reduce costs and{' '}
        <Link 
          to="/reduce-claim-denials-healthcare-billing"
          style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
        >
          reduce claim denials
        </Link>. Our proven{' '}
        <Link 
          to="/revenue-cycle-management-independent-physicians"
          style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
        >
          revenue cycle management
        </Link>{' '}
        delivers measurable results:
      </p>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '50px',
          flexWrap: 'wrap',
          maxWidth: '1300px',
          margin: '0 auto 50px'
        }}
      >
        <RoiCircle 
          targetPercent={45} 
          text="Reduce claim denials with expert management" 
        />
        <RoiCircle 
          targetPercent={50} 
          text="Save on billing operational costs" 
        />
        <RoiCircle 
          targetPercent={35} 
          text="Increase net collections for small practices" 
        />
      </div>

      {/* ROI Statement - SEO OPTIMIZED with NEW KEYWORDS */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '40px 30px',
        borderRadius: '12px',
        maxWidth: '900px',
        margin: '0 auto 40px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
      }}>
        <p
          style={{
            fontSize: '1.8rem',
            fontWeight: '700',
            color: '#0077b6',
            marginBottom: '15px',
          }}
        >
          30% to 250% ROI 
        </p>
        <p style={{ 
          fontSize: '1.3rem',
          fontWeight: '500',
          color: '#555',
          lineHeight: '1.6'
        }}>
          Every year for small practices and independent physicians using our{' '}
          <Link 
            to="/medical-billing-outsourcing-small-practices"
            style={{ color: '#0077b6', textDecoration: 'underline' }}
          >
            medical billing outsourcing services
          </Link>
        </p>
      </div>

      {/* Benefits section with NEW KEYWORDS */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '35px 30px',
        borderRadius: '12px',
        maxWidth: '1000px',
        margin: '0 auto',
        textAlign: 'left',
      }}>
        <h3 style={{
          fontSize: '1.6rem',
          fontWeight: '600',
          marginBottom: '20px',
          color: '#0077b6',
          textAlign: 'center',
        }}>
          Proven Benefits of Medical Billing Outsourcing for Clinics
        </h3>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '25px',
          marginTop: '25px',
        }}>
          <div style={{ padding: '15px' }}>
            <h4 style={{ 
              fontSize: '1.2rem', 
              fontWeight: '600', 
              color: '#0077b6',
              marginBottom: '10px'
            }}>
              ⚡ Faster Payments
            </h4>
            <p style={{ fontSize: '1rem', lineHeight: '1.7', color: '#555' }}>
              <Link 
                to="/revenue-cycle-management-independent-physicians"
                style={{ color: '#0077b6', textDecoration: 'underline' }}
              >
                Independent physicians
              </Link>{' '}
              receive payments 15-20 days faster with expert billing outsourcing vs 45-60 day in-house cycles.
            </p>
          </div>

          <div style={{ padding: '15px' }}>
            <h4 style={{ 
              fontSize: '1.2rem', 
              fontWeight: '600', 
              color: '#0077b6',
              marginBottom: '10px'
            }}>
              📉 Lower Denial Rates
            </h4>
            <p style={{ fontSize: '1rem', lineHeight: '1.7', color: '#555' }}>
              Our{' '}
              <Link 
                to="/denial-management-services-medical-clinics"
                style={{ color: '#0077b6', textDecoration: 'underline' }}
              >
                denial management services
              </Link>{' '}
              help small practices{' '}
              <Link 
                to="/reduce-claim-denials-healthcare-billing"
                style={{ color: '#0077b6', textDecoration: 'underline' }}
              >
                reduce claim denials
              </Link>{' '}
              by 35-45%.
            </p>
          </div>

          <div style={{ padding: '15px' }}>
            <h4 style={{ 
              fontSize: '1.2rem', 
              fontWeight: '600', 
              color: '#0077b6',
              marginBottom: '10px'
            }}>
              💰 Cost Savings
            </h4>
            <p style={{ fontSize: '1rem', lineHeight: '1.7', color: '#555' }}>
              <Link 
                to="/medical-billing-outsourcing-small-practices"
                style={{ color: '#0077b6', textDecoration: 'underline' }}
              >
                Small practices
              </Link>{' '}
              save 30-50% on billing costs by eliminating in-house staff overhead and technology expenses.
            </p>
          </div>
        </div>

        {/* Additional benefits for multispecialty */}
        <div style={{
          marginTop: '30px',
          padding: '20px',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e0e0e0',
        }}>
          <h4 style={{
            fontSize: '1.1rem',
            fontWeight: '600',
            color: '#2e7d32',
            marginBottom: '12px',
            textAlign: 'center',
          }}>
            🏢 Special Benefits for Multispecialty Practices
          </h4>
          <p style={{
            fontSize: '1rem',
            lineHeight: '1.7',
            color: '#555',
            textAlign: 'center',
          }}>
            Our{' '}
            <Link 
              to="/revenue-cycle-management-multispecialty-practices"
              style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
            >
              revenue cycle management for multispecialty practices
            </Link>{' '}
            handles complex coding across multiple specialties, manages diverse payer contracts, and ensures compliance with specialty-specific regulations.
          </p>
        </div>

        <p style={{
          fontSize: '1.05rem',
          textAlign: 'center',
          marginTop: '30px',
          color: '#666',
          fontStyle: 'italic',
        }}>
          Join 500+ small practices and independent physicians experiencing the{' '}
          <Link 
            to="/benefits-medical-billing-outsourcing-clinics"
            style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
          >
            benefits of medical billing outsourcing
          </Link>.
        </p>
      </div>

      {/* Mobile Responsive */}
      <style>
        {`
          @media (max-width: 768px) {
            section {
              padding: 60px 4%;
            }

            div[style*='width: 260px'] {
              width: 220px !important;
              height: 220px !important;
            }

            div[style*='gridTemplateColumns'] {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </section>
  );
};

export default RoiImpactSection;